import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import Control from "../../../../common/Control";
import SelectMultiple from "../../../../common/SelectMultiple";
import TagInput from "../../../../common/TagInput";
import DateRangePicker from "../../../../common/DateRangePicker";

const FilterPopover = ({
  isOpen,
  onClose,
  onApply,
  onClear,
  filterType,
  currentValue,
  options = [],
  placeholder = "",
  helpers,
  buttonRef,
  label = "",
}) => {
  const [value, setValue] = useState(currentValue || "");
  const popoverRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setValue(currentValue || "");
      // Donner le focus à l'input après un petit délai
      setTimeout(() => {
        if (inputRef.current) {
          const input = inputRef.current.querySelector(
            "input, select, textarea",
          );
          if (input) {
            input.focus();
          }
        }
      }, 100);
    }
  }, [isOpen, currentValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, onClose, buttonRef]);

  // Fonction pour valider qu'une date est complète (yyyy-mm-dd) ou vide
  const isValidDateValue = (dateString) => {
    if (!dateString || dateString.trim() === "") return true;
    // Vérifier que la date est au format complet yyyy-mm-dd (10 caractères)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    return dateRegex.test(dateString);
  };

  // Fonction pour appliquer automatiquement les filtres
  const handleAutoApply = (newValue) => {
    setValue(newValue);
    onApply(newValue);
  };

  // Fonction de debounce pour les inputs texte
  const handleDebouncedAutoApply = useCallback(
    (newValue) => {
      setValue(newValue);
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        onApply(newValue);
      }, 300);
    },
    [onApply],
  );

  // Nettoyer le timeout au démontage
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleClear = () => {
    setValue("");
    onClear();
    onClose();
  };

  const handleKeyPress = (e) => {
    // Les filtres s'appliquent désormais automatiquement, Enter ferme juste le popover
    // sauf pour les tag-input où Enter sert à ajouter des tags
    if (e.key === "Enter" && filterType !== "tag-input") {
      e.preventDefault();
      onClose();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  const getPopoverStyle = () => {
    if (!buttonRef.current) return {};

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const popoverHeight = 250;

    let top = buttonRect.bottom + 10;
    let left = buttonRect.left;

    // Ajustements si le popover sort de l'écran
    if (top + popoverHeight > window.innerHeight) {
      top = buttonRect.top - popoverHeight - 10;
    }

    if (left + 350 > window.innerWidth) {
      left = window.innerWidth - 360;
    }

    if (left < 10) {
      left = 10;
    }

    return {
      position: "fixed",
      top: `${top}px`,
      left: `${left}px`,
      zIndex: 1050,
      minWidth: "320px",
      maxWidth: "400px",
    };
  };

  const renderFilterContent = () => {
    switch (filterType) {
      case "text":
        return (
          <Control
            type="text"
            name="filter"
            label=""
            value={value}
            change={(e) => {
              const newValue = e.target.value;
              handleDebouncedAutoApply(newValue);
            }}
            placeholder={placeholder}
            className="mb-0"
          />
        );

      case "select":
        return (
          <div className="form-group mb-0">
            <select
              className="form-control"
              value={value || ""}
              onChange={(e) => handleAutoApply(e.target.value)}
            >
              <option value="">{placeholder || "Sélectionner..."}</option>
              {(options || []).map((opt) => {
                if (opt && typeof opt === "object") {
                  return (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                    </option>
                  );
                }
                return (
                  <option key={`${opt}`} value={`${opt}`}>
                    {opt}
                  </option>
                );
              })}
            </select>
          </div>
        );

      case "select-multiple":
        return (
          <>
            {Array.isArray(helpers) && helpers.length > 0 && (
              <div className="d-flex flex-wrap mb-2" style={{ gap: 4 }}>
                {helpers.map((helper, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="btn btn-sm btn-default"
                    onClick={() => handleAutoApply(helper.compute(options))}
                  >
                    {helper.label}
                  </button>
                ))}
              </div>
            )}
            <SelectMultiple
              name="filter"
              label=""
              datas={options}
              dataIndex="id"
              dataLabel="name"
              placeholder={placeholder}
              value={value || []}
              change={(e) => handleAutoApply(e.target.value)}
              className="mb-0"
            />
          </>
        );

      case "tag-input":
        return (
          <TagInput
            name="filter"
            label=""
            placeholder={placeholder}
            value={value || []}
            onChange={(e) => handleAutoApply(e.target.value)}
            className="mb-0"
            tagClassName="fs-6"
          />
        );

      case "date-range":
        return (
          <DateRangePicker
            name="filter"
            label=""
            placeholder={placeholder}
            value={value || []}
            onChange={(e) => {
              const dateRange = e.target.value;
              // Valider que les dates sont complètes ou vides
              const fromValid = isValidDateValue(dateRange?.from);
              const toValid = isValidDateValue(dateRange?.to);

              // N'appliquer le filtre que si les deux dates sont valides
              if (fromValid && toValid) {
                handleAutoApply(dateRange);
              } else {
                // Mettre à jour la valeur locale sans appliquer le filtre
                setValue(dateRange);
              }
            }}
            className="mb-0"
          />
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  const popoverContent = (
    <div
      ref={popoverRef}
      className="bg-white border rounded shadow-lg p-3"
      style={getPopoverStyle()}
      onKeyDown={(e) => {
        // Ne traiter les événements que s'ils n'ont pas été arrêtés par propagation
        // et qu'ils ne viennent pas des TagInput
        if (!e.defaultPrevented && !e.isTagInputEvent) {
          handleKeyPress(e);
        }
      }}
    >
      {/* Flèche pointant vers le header */}
      <div
        className="position-absolute"
        style={{
          top: "-8px",
          left: "20px",
          width: 0,
          height: 0,
          borderLeft: "8px solid transparent",
          borderRight: "8px solid transparent",
          borderBottom: "8px solid white",
        }}
      />
      <div
        className="position-absolute"
        style={{
          top: "-9px",
          left: "19px",
          width: 0,
          height: 0,
          borderLeft: "9px solid transparent",
          borderRight: "9px solid transparent",
          borderBottom: "9px solid #dee2e6",
        }}
      />

      <div className="mb-3">
        <h6 className="mb-2">{label}</h6>
        <div
          ref={inputRef}
          onKeyDown={(e) => {
            // Empêcher la propagation des événements clavier pour les tag-input
            if (filterType === "tag-input" && e.key === "Enter") {
              e.stopPropagation();
              e.preventDefault();
              // Marquer l'événement comme traité
              e.isTagInputEvent = true;
            }
          }}
        >
          {renderFilterContent()}
        </div>
      </div>

      <div className="d-flex align-items-center justify-content-between">
        <button
          type="button"
          className="btn btn-sm btn-default"
          onClick={handleClear}
        >
          <i className="fa fa-times mr-1" />
          Effacer
        </button>
        <button
          type="button"
          className="btn btn-sm btn-default"
          onClick={onClose}
        >
          Fermer
        </button>
      </div>
    </div>
  );

  return createPortal(popoverContent, document.body);
};

export default FilterPopover;
